import { api } from "services/api";
import { createSlice } from "@reduxjs/toolkit";
import {
  encrypt,
  getDataFromLocalStorage,
  storeLocalStorageData,
} from "utils/helpers";
import moment from "moment";

const initialState = {
  isShowProjectPopup: false,
  projectEditData: null,
  profileImage: "",
  accountData: null,
  networkType: null,
  copyData: null,
  errorData: null,
  showSubscriptionPopup: false,
  paymentOptionList: null,
  isSubscription: false,
  globalChainID: 0,
  isDarkMode: false,
  showSubscriptionExpirePopup: false,
  subscriptionEndDate: null,
  remainingDays: 0,
  isOpen: false,
};

const globalSlice = createSlice({
  name: "global",
  initialState,
  reducers: {
    setIsShowProjectPopup(state, action) {
      state.isShowProjectPopup = action.payload;
    },
    setProjectEditData(state, action) {
      state.projectEditData = action.payload;
    },
    setProfileImage(state, action) {
      state.profileImage = action.payload;
    },
    setAccountData(state, action) {
      state.accountData = action.payload;
    },
    setRemainingDays(state, action) {
      state.remainingDays = action.payload;
    },
    setNetworkType(state, action) {
      state.networkType = action.payload;
    },
    setCopyData(state, action) {
      state.copyData = action.payload;
    },
    setErrorData(state, action) {
      state.errorData = action.payload;
    },
    setShowSubscriptionPopup(state, action) {
      state.showSubscriptionPopup = action.payload;
    },
    setShowSubscriptionExpirePopup(state, action) {
      state.showSubscriptionExpirePopup = action.payload;
    },
    setSubscriptionEndDate(state, action) {
      state.subscriptionEndDate = action.payload;
    },
    setPaymentOptionList(state, action) {
      state.paymentOptionList = action.payload;
    },
    setIsSubscription(state, action) {
      state.isSubscription = action.payload;
    },
    setGlobalChainID(state, action) {
      state.globalChainID = action.payload;
    },
    setIsDarkMode(state, action) {
      state.isDarkMode = action.payload;
    },
    setIsOpen(state, action) {
      state.isOpen = action.payload;
    },
    resetAllState(state) {
      state.isShowProjectPopup = false;
      state.projectEditData = null;
      state.profileImage = "";
      state.accountData = null;
      state.networkType = null;
      state.copyData = null;
      state.errorData = null;
      state.showSubscriptionPopup = false;
      state.paymentOptionList = null;
      state.isSubscription = false;
      state.globalChainID = 0;
      state.isDarkMode = false;
      state.isOpen = false;
    },
  },
});

export const getSignatureMessage = (walletAddress) => async (dispatch) => {
  try {
    const res = await api.get(`/users/${walletAddress}/message`);
    return await dispatch(handelResponse(res));
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const handleLogin = (payload) => async (dispatch) => {
  try {
    const res = await api.post("/signin", payload, {});
    if (res?.status === 201) {
      storeLocalStorageData({ ...res?.data, address: payload.address });
      dispatch(
        setAccountData(encrypt({ ...res?.data, address: payload.address }))
      );
    }
    return res;
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const getUserDetails = () => async (dispatch) => {
  try {
    const res = await api.get("/user");
    const respons = await dispatch(handelResponse(res));
    const resData = respons?.data || {};
    storeLocalStorageData(resData);
    dispatch(
      setAccountData(
        encrypt({
          ...resData,
          access_token: getDataFromLocalStorage("access_token"),
        })
      )
    );
    return respons;
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const setUserDetails = () => async (dispatch) => {
  try {
    const res = await api.post(`/user`, {});
    return await dispatch(handelResponse(res));
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const updateUserDetails = (userID, payload) => async (dispatch) => {
  try {
    const res = await api.put(`/user/${userID}`, payload);
    return await dispatch(handelResponse(res));
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const getNetwork = () => async (dispatch) => {
  try {
    const res = await api.get("/network");
    const respons = await dispatch(handelResponse(res));
    dispatch(setNetworkType(respons?.data || []));
    return respons;
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const getProjects = () => async (dispatch) => {
  try {
    const res = await api.get("/project");
    return await dispatch(handelResponse(res));
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const addProject = (payload) => async (dispatch) => {
  try {
    const res = await api.post("/project", payload);
    return await dispatch(handelResponse(res));
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const updateProject = (projectID, payload) => async (dispatch) => {
  try {
    const res = await api.put(`/project/${projectID}`, payload);
    return await dispatch(handelResponse(res));
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const deleteProjects = (projectID) => async (dispatch) => {
  try {
    const res = await api.delete(`/project/${projectID}`);
    return await dispatch(handelResponse(res));
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const deleteAccount = () => async (dispatch) => {
  try {
    const res = await api.delete(`/account/delete`);
    return await dispatch(handelResponse(res));
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const getSubscription = () => async (dispatch) => {
  try {
    const res = await api.get("/subscription/tier");

    return await dispatch(handelResponse(res));
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const addSubscription = (payload) => async (dispatch) => {
  try {
    const res = await api.post("/subscription", payload);
    return await dispatch(handelResponse(res));
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const updateLogging = (projectID, queryParams) => async (dispatch) => {
  try {
    const res = await api.put(`/project/logging/${projectID}?${queryParams}`);
    return await dispatch(handelResponse(res));
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const getMySubscription = () => async (dispatch) => {
  try {
    const res = await api.get("/subscription");
    const response = await dispatch(handelResponse(res));
    if (response?.data?.sub_id) {
      storeLocalStorageData({
        subscription: res?.data,
      });
      dispatch(
        setAccountData(
          encrypt({
            ...getDataFromLocalStorage(),
            subscription: res?.data,
          })
        )
      );
      if (
        response?.data?.sub_tier_id &&
        response?.data?.status === "active" &&
        response?.data?.sub_end_date
      ) {
        const subEndDate = moment(
          response?.data?.sub_end_date,
          "YYYY-MM-DDTHH:mm:ss.SSSZ"
        );
        const currentDate = moment();
        const remainingDay = subEndDate.diff(currentDate, "days");
        const remainingMinutes = subEndDate.diff(currentDate, "minutes");
        if (remainingDay >= 0 && remainingMinutes > 0) {
          dispatch(setIsSubscription(true));
        }
        dispatch(setSubscriptionEndDate(response?.data?.sub_end_date));
      } else {
        if (!localStorage.initShowSubscriptionPopup) {
          localStorage.initShowSubscriptionPopup = 1;
          dispatch(setShowSubscriptionPopup(true));
        }
      }
    } else {
      storeLocalStorageData({
        subscription: null,
      });
      dispatch(setIsSubscription(false));
      dispatch(
        setAccountData(
          encrypt({
            ...getDataFromLocalStorage(),
            subscription: null,
          })
        )
      );
      if (!localStorage.initShowSubscriptionPopup) {
        localStorage.initShowSubscriptionPopup = 1;
        dispatch(setShowSubscriptionPopup(true));
      }
    }

    return response;
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const getSubscriptionOptions = () => async (dispatch) => {
  try {
    const res = await api.get("/subscription/payment");
    const response = await dispatch(handelResponse(res));
    dispatch(setPaymentOptionList(response?.data || []));
    return response;
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const addTransactions = (payload) => async (dispatch) => {
  try {
    const res = await api.post("/transactions", payload);
    return await dispatch(handelResponse(res));
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const getDiscount = () => async (dispatch) => {
  try {
    const res = await api.get("/discounts/1");
    return await dispatch(handelResponse(res));
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const getQueryLogs = () => async (dispatch) => {
  try {
    const res = await api.get("/query/stats");
    return await dispatch(handelResponse(res));
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const getDeletedProject = () => async (dispatch) => {
  try {
    const res = await api.get("/deleted-project");
    return await dispatch(handelResponse(res));
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const getTransactionHistory = (id) => async (dispatch) => {
  try {
    const res = await api.get(`/subscription/history?chain_id=${id}`);
    return await dispatch(handelResponse(res));
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const handleVerifyPassword = (payload) => async (dispatch) => {
  try {
    const res = await api.post("/password", payload);
    return await dispatch(handelResponse(res));
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const addFeedback = (payload) => async (dispatch) => {
  try {
    const res = await api.post("/feedback/task", payload);
    return await dispatch(handelResponse(res));
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const attachToFeedback = (id, payload) => async (dispatch) => {
  try {
    const res = await api.post(`/feedback/attachment?taskid=${id}`, payload, {
      "Content-Type": "multipart/form-data",
    });
    return await dispatch(handelResponse(res));
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const createNowPaymentInvoice = (payload) => async (dispatch) => {
  try {
    const res = await api.post("/nowpayments/v1/invoice", payload);
    return await dispatch(handelResponse(res));
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const upadateNowPayment = (param) => async (dispatch) => {
  try {
    const res = await api.post(`/nowpayments/payment/${param}`);
    return await dispatch(handelResponse(res));
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};
export const downloadLogs = (param) => async (dispatch) => {
  try {
    const res = await api.get(`/logs/download?${param}`, {
      "Content-Type": "text/csv",
    });
    return await dispatch(handelResponse(res));
  } catch (error) {
    return dispatch(handelCatch(error));
  }
};

export const handelResponse = (res) => async (dispatch) => {
  let returnValue = null;
  const status = res?.status;
  switch (status) {
    case 200:
      returnValue = res;
      break;
    case 201:
      returnValue = res;
      break;
    case 204:
      returnValue = {
        status: status,
        data: [],
      };
      break;
    case 400:
      throwError({ message: "Something went wrong!" });
      break;
    default:
      throwError({ message: "Something went wrong!" });
      returnValue = {
        status: status,
        message: "Something went wrong!",
      };
      break;
  }
  return returnValue;
};

export const handelCatch = (error) => async (dispatch) => {
  let status = error?.response?.status;
  let messsage = error?.response?.data || "Something went wrong!";
  let returnCatch = {
    status: status,
    messsage: messsage,
  };
  if (status === 401) {
    dispatch(throwError("Session is expired"));
    dispatch(setAccountData(null));
    dispatch(setShowSubscriptionPopup(false));
    localStorage.removeItem("accountData");
    localStorage.removeItem("theme");
  } else if (status === 403) {
    messsage =
      error?.response?.data?.message ||
      error?.response?.data ||
      "Something went wrong!";
    dispatch(
      setErrorData({
        show: true,
        message: messsage,
        type: "danger",
      })
    );
  } else if (status === 409) {
    dispatch(
      setErrorData({
        show: false,
        message: messsage,
        type: "danger",
      })
    );
  } else if (status === 404) {
    dispatch(
      setErrorData({
        show: true,
        message: messsage,
        type: "danger",
      })
    );
  } else if (status === 400) {
    messsage = error?.response?.data?.message || "Something went wrong!";
    dispatch(
      setErrorData({
        show: true,
        message: messsage,
        type: "danger",
      })
    );
  } else if (status === 500) {
    messsage = error?.response?.data?.message || "Something went wrong!";
    dispatch(
      setErrorData({
        show: true,
        message: messsage,
        type: "danger",
      })
    );
  } else {
    dispatch(
      setErrorData({
        show: true,
        message: messsage,
        type: "danger",
      })
    );
  }
  return returnCatch;
};

export const showSuccess = (message) => async (dispatch) => {
  dispatch(
    setErrorData({
      show: true,
      message: message,
      type: "success",
    })
  );
};

export const throwError = (message) => async (dispatch) => {
  let newMessage = message;
  newMessage = message || "Something went wrong!";
  dispatch(
    setErrorData({
      show: true,
      message: newMessage,
      type: "danger",
    })
  );
};

export const {
  setIsShowProjectPopup,
  setProjectEditData,
  setProfileImage,
  setAccountData,
  setNetworkType,
  setCopyData,
  setErrorData,
  setShowSubscriptionPopup,
  setPaymentOptionList,
  setIsSubscription,
  resetAllState,
  setGlobalChainID,
  setIsDarkMode,
  setShowSubscriptionExpirePopup,
  setSubscriptionEndDate,
  setRemainingDays,
  setIsOpen,
} = globalSlice.actions;

export default globalSlice.reducer;
